/**
 * THE MAP'S FILTER, SHARED WITH THE HOMES UNDER IT (W1, Muse site audit
 * 2026-10-20: "after filtering, the map legend updates but the dial header
 * does not").
 *
 * V3Atlas keeps its type toggles and price ceiling in its own state and reads
 * them through one predicate (isOn) for every mark, count and key. A place
 * page's homes block (PlaceSubdivisionHomes) sat beside it with only the
 * subdivision selection in common, so turning "Land" off moved the legend and
 * left the dials and their headers counting land. V3Atlas now reports its
 * filter (onFilterChange) and the homes block applies this predicate, the same
 * rule over the same type keys (classifyType, as atlasDotsFromTiles assigns
 * them) and the same ask, so the dials and their headers follow the map.
 *
 * Client-safe: pure functions over plain rows.
 */
import { classifyType } from '@/app/_v3/home-field-items'
import type { ListingCountBasis } from '@/lib/listing-counts'

export type AtlasFilterState = {
  /** Atlas type keys the reader turned off ('land', 'condo', ...). */
  offTypes: readonly string[]
  /** The price ceiling, or null at any price. */
  maxPrice: number | null
  /**
   * The basis the map's key names for this filter (mapListingCount: "house
   * and condo only"), so a count of the filtered homes names the same one.
   */
  basis?: ListingCountBasis
}

export const ATLAS_FILTER_NONE: AtlasFilterState = Object.freeze({ offTypes: [], maxPrice: null })

export function atlasFilterIsActive(filter: AtlasFilterState | null | undefined): boolean {
  return Boolean(filter && (filter.offTypes.length > 0 || filter.maxPrice != null))
}

/** A stable key for a filter, so a dial re-keys (and re-opens on its first home) when it changes. */
export function atlasFilterKey(filter: AtlasFilterState | null | undefined): string {
  if (!atlasFilterIsActive(filter)) return 'any'
  return `${[...filter!.offTypes].sort().join('+')}|${filter!.maxPrice ?? ''}`
}

export type AtlasFilterRow = {
  propertyType?: string | null
  propertySubType?: string | null
  price?: number | null
}

/**
 * Whether the map's filter keeps a listing row. Mirrors V3Atlas isOn: the type
 * is on, and the ask is at or under the ceiling (a row with no published ask
 * stays, as a dot with no price does).
 */
export function atlasFilterAdmits(row: AtlasFilterRow, filter: AtlasFilterState | null | undefined): boolean {
  if (!atlasFilterIsActive(filter)) return true
  const { typeKey } = classifyType({ propertyType: row.propertyType, propertySubType: row.propertySubType })
  if (filter!.offTypes.includes(typeKey)) return false
  const p = row.price
  if (filter!.maxPrice != null && p != null && Number.isFinite(p) && p > 0 && Math.round(p) > filter!.maxPrice) {
    return false
  }
  return true
}

export function atlasFilterRows<R extends AtlasFilterRow>(
  rows: readonly R[],
  filter: AtlasFilterState | null | undefined,
): readonly R[] {
  if (!atlasFilterIsActive(filter)) return rows
  return rows.filter((row) => atlasFilterAdmits(row, filter))
}
